
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { auth, db } from "@/firebase";

function clean(value) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

export function supplierKey(value) {
  return clean(value).toLocaleLowerCase("en-GB");
}

export function normaliseSupplierNames(values) {
  const names = [];
  const seen = new Set();

  for (const raw of Array.isArray(values) ? values : []) {
    const name = clean(
      typeof raw === "string"
        ? raw
        : raw?.name
    );

    const id = supplierKey(name);

    if (id && !seen.has(id)) {
      seen.add(id);
      names.push(name);
    }
  }

  return names;
}

export function normaliseGroupRules(value) {
  const result = {};

  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value)
  ) {
    return result;
  }

  for (const [rawGroup, rawRule] of Object.entries(value)) {
    const group = clean(rawGroup);

    if (
      !group ||
      !rawRule ||
      typeof rawRule !== "object" ||
      Array.isArray(rawRule)
    ) {
      continue;
    }

    const suppliers = normaliseSupplierNames(
      rawRule.suppliers
    );

    const requestedPrimary = clean(
      rawRule.primarySupplier
    );

    const primarySupplier =
      suppliers.find(
        (name) =>
          supplierKey(name) ===
          supplierKey(requestedPrimary)
      ) ||
      suppliers[0] ||
      "";

    result[group] = {
      suppliers,
      primarySupplier,
    };
  }

  return result;
}

function requireUser() {
  const user = auth.currentUser;

  if (!user) {
    throw new Error(
      "You must be signed in to access venue suppliers."
    );
  }

  return user;
}

async function requireOperations() {
  const user = requireUser();

  const snapshot = await getDoc(
    doc(db, "users", user.uid)
  );

  if (
    !snapshot.exists() ||
    snapshot.data()?.isOperations !== true
  ) {
    throw new Error(
      "Supplier Access management is restricted to Operations."
    );
  }

  return user;
}

function accessRef(venueId) {
  const id = clean(venueId);

  if (!id || id.includes("/")) {
    throw new Error("A valid venue ID is required.");
  }

  return doc(
    db,
    "venues",
    id,
    "stock_settings",
    "supplier_access"
  );
}

export async function getVenueSupplierAccess(venueId) {
  const id = clean(venueId);

  if (!id) {
    return {
      configured: false,
      suppliers: [],
      groupRules: {},
    };
  }

  requireUser();

  const snapshot = await getDoc(accessRef(id));

  if (!snapshot.exists()) {
    return {
      configured: false,
      suppliers: [],
      groupRules: {},
    };
  }

  const data = snapshot.data() || {};

  return {
    configured: true,
    suppliers: normaliseSupplierNames(data.suppliers),
    groupRules: normaliseGroupRules(data.groupRules),
  };
}

export async function listVenueSupplierAccess() {
  await requireOperations();

  const snapshot = await getDocs(
    collection(db, "venues")
  );

  const venues = await Promise.all(
    snapshot.docs.map(async (venueSnapshot) => {
      const venue = venueSnapshot.data() || {};

      const access = await getVenueSupplierAccess(
        venueSnapshot.id
      );

      return {
        id: venueSnapshot.id,

        name: clean(
          venue.venueName ||
          venue.name ||
          venue.displayName ||
          venue.title ||
          venueSnapshot.id
        ),

        ...access,
      };
    })
  );

  return venues.sort((a, b) =>
    a.name.localeCompare(b.name, "en-GB")
  );
}

export async function saveVenueSupplierAccess(
  venueId,
  suppliers,
  groupRules = undefined
) {
  const user = await requireOperations();

  const names = normaliseSupplierNames(suppliers);

  if (names.length > 100) {
    throw new Error("Too many suppliers selected.");
  }

  const payload = {
    suppliers: names,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  };

  if (groupRules !== undefined) {
    const rules = normaliseGroupRules(groupRules);

    if (Object.keys(rules).length > 200) {
      throw new Error("Too many product group rules.");
    }

    // No product-group rule can enable a supplier
    // which is disabled at venue level.
    for (const rule of Object.values(rules)) {
      rule.suppliers = rule.suppliers.filter(
        (supplier) =>
          names.some(
            (name) =>
              supplierKey(name) ===
              supplierKey(supplier)
          )
      );

      if (
        !rule.suppliers.some(
          (supplier) =>
            supplierKey(supplier) ===
            supplierKey(rule.primarySupplier)
        )
      ) {
        rule.primarySupplier =
          rule.suppliers[0] || "";
      }
    }

    payload.groupRules = rules;
  }

  // Explicit mergeFields replaces the complete groupRules
  // map when supplied, including deleted overrides.
  await setDoc(
    accessRef(venueId),
    payload,
    {
      mergeFields: Object.keys(payload),
    }
  );

  // Existing callers expect a supplier-name array.
  return names;
}

export function getGroupRule(access, productGroup) {
  const wanted = supplierKey(productGroup);

  const rules = normaliseGroupRules(
    access?.groupRules
  );

  for (const [group, rule] of Object.entries(rules)) {
    if (supplierKey(group) === wanted) {
      return rule;
    }
  }

  return null;
}

export function getAllowedSuppliersForGroup(
  access,
  productGroup
) {
  if (!access || access.configured !== true) {
    // Null means the venue is not yet configured:
    // preserve legacy access.
    return null;
  }

  const venueSuppliers = normaliseSupplierNames(
    access.suppliers
  );

  const rule = getGroupRule(
    access,
    productGroup
  );

  if (!rule) {
    return venueSuppliers;
  }

  // Product group rules are more restrictive than
  // venue-level supplier selections.
  return venueSuppliers.filter(
    (supplier) =>
      rule.suppliers.some(
        (allowed) =>
          supplierKey(allowed) ===
          supplierKey(supplier)
      )
  );
}

export function supplierAllowed(
  access,
  supplier,
  productGroup = ""
) {
  const permitted = getAllowedSuppliersForGroup(
    access,
    productGroup
  );

  if (permitted === null) {
    return true;
  }

  return permitted.some(
    (allowed) =>
      supplierKey(allowed) ===
      supplierKey(supplier)
  );
}

export function getPrimarySupplierForGroup(
  access,
  productGroup
) {
  const permitted = getAllowedSuppliersForGroup(
    access,
    productGroup
  );

  if (!permitted?.length) {
    return "";
  }

  const rule = getGroupRule(
    access,
    productGroup
  );

  if (rule?.primarySupplier) {
    const primary = permitted.find(
      (name) =>
        supplierKey(name) ===
        supplierKey(rule.primarySupplier)
    );

    if (primary) {
      return primary;
    }
  }

  return permitted[0];
}

export function supplierPriority(
  access,
  supplier,
  productGroup = ""
) {
  const primary = getPrimarySupplierForGroup(
    access,
    productGroup
  );

  if (
    primary &&
    supplierKey(primary) === supplierKey(supplier)
  ) {
    return 0;
  }

  const permitted = getAllowedSuppliersForGroup(
    access,
    productGroup
  );

  if (permitted === null) {
    return 0;
  }

  const index = permitted.findIndex(
    (name) =>
      supplierKey(name) ===
      supplierKey(supplier)
  );

  return index < 0
    ? Number.MAX_SAFE_INTEGER
    : index + 1;
}

export function filterProductsForVenue(
  products,
  access
) {
  const rows = Array.isArray(products)
    ? products
    : [];

  if (!access || access.configured !== true) {
    return [...rows];
  }

  return rows.filter(
    (product) =>
      supplierAllowed(
        access,
        product.supplier,
        product.product_group
      )
  );
}
